import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import type {ComparisonRangeType, RowType} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {StepIcon} from 'shells/icon/svg/step-icon';
import {Body} from 'shells/typography/body';

import type {NutshellState} from '../../../store';

import {ReportSummaryTable} from '../report-summary-table';

import {requestTaskReportSummary} from './task-report-actions';
import {
    getSummaryData,
    getSummaryIsLoading,
    getSummaryHasError,
    getSummaryTotals,
} from './task-report-selectors';

import styles from './tasks-report-summary.module.css';

type Props = {
    filters: any;
    comparisonType?: ComparisonRangeType;
};

type CellBodyProps = {
    rowType: RowType;
    segmentTitle?: string;
    children: React.ReactNode;
    isLabel?: boolean;
};

function CellBody(props: CellBodyProps) {
    const {rowType, segmentTitle, children} = props;
    const isGroup = rowType === 'group';
    const isTotal = rowType === 'total';

    const color = React.useMemo(() => {
        if (segmentTitle === 'Completed on time') {
            return colors.leadHovered;
        } else if (segmentTitle === 'Not completed') {
            return colors.roseDk;
        } else {
            return undefined;
        }
    }, [segmentTitle]);

    return (
        <span style={{color}} data-row-type={rowType}>
            <Body
                size='small'
                isBold={(isGroup && props.isLabel) || isTotal}
                color={color ? 'inherit' : 'dark'}
                truncate={{type: 'block'}}
            >
                {children}
            </Body>
        </span>
    );
}

export function TasksReportSummary(props: Props) {
    const {filters, comparisonType} = props;

    const data = useSelector((state: NutshellState) => getSummaryData(state));
    const totals = useSelector((state: NutshellState) => getSummaryTotals(state));
    const isLoading = useSelector((state: NutshellState) => getSummaryIsLoading(state));
    const hasError = useSelector((state: NutshellState) => getSummaryHasError(state));
    const dispatch = useDispatch();

    const initialRequestRef = React.useRef<boolean>(false);
    const prevFiltersRef = React.useRef<any | null | undefined>(null);
    const prevComparisonTypeRef = React.useRef<ComparisonRangeType | null | undefined>(null);

    React.useEffect(() => {
        if (!isLoading) {
            ReactTooltip.rebuild();
        }
    }, [isLoading]);

    React.useEffect(() => {
        const prevFilters = prevFiltersRef.current;
        const prevComparisonType = prevComparisonTypeRef.current;

        if (
            !initialRequestRef.current ||
            JSON.stringify(filters) !== JSON.stringify(prevFilters) ||
            comparisonType !== prevComparisonType
        ) {
            dispatch(requestTaskReportSummary({filters, comparisonType}));
            initialRequestRef.current = true;
        }

        prevFiltersRef.current = filters;
        prevComparisonTypeRef.current = comparisonType;
    }, [dispatch, filters, comparisonType]);

    // If updating the width of these columns, need to update
    // .routed-report-content--tasks min-width in less/report.less
    const columns = React.useMemo(() => {
        const cols = [
            {
                Header: 'Task name',
                id: 'stage-task-user',
                accessor: (currentBucket: any) => {
                    return currentBucket;
                },
                Cell: ({cell}: any) => {
                    const isItem = cell.value.rowType === 'item';
                    const isGroup = cell.value.rowType === 'group';
                    const isLong = cell.value.title.length > 25;

                    const classNames = classnames({
                        'ml-16': isItem,
                        'flex flex-dir-row gap-8 align-center': isGroup,
                    });

                    return (
                        <div
                            className={classNames}
                            // Hack to show tooltips for truncated text, we just
                            // use this heuristic
                            data-tip={isLong ? cell.value.title : undefined}
                        >
                            {isGroup && (
                                <StepIcon size={16} color={colors.greyDk} wrapWithDiv={false} />
                            )}
                            <CellBody rowType={cell.value.rowType} isLabel={true}>
                                {cell.value.title}
                            </CellBody>
                        </div>
                    );
                },
                dataType: 'component',
                width: 240,
                shouldTruncate: true,
                Footer: <div data-row-type='total'>Totals across stages</div>,
            },
        ];

        const segmentNames =
            data &&
            data.currentPeriod &&
            data.currentPeriod[0] &&
            data.currentPeriod[0].segments &&
            // @ts-expect-error
            data.currentPeriod[0].segments.map((segment) => segment.title);

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        segmentNames &&
            // @ts-expect-error
            segmentNames.forEach((segmentName) => {
                cols.push({
                    Header: segmentName,
                    id: segmentName,
                    accessor: (currentBucket: any) => {
                        return currentBucket;
                    },
                    dataType: 'component',
                    rightAlign: true,
                    width: 135,
                    canResize: false,
                    Cell: ({cell}: any) => {
                        const matchingSegment = cell.value.segments.find(
                            // @ts-expect-error
                            (segment) => segment.title === segmentName
                        );
                        let matchingSegmentCount =
                            matchingSegment &&
                            matchingSegment.totalsByReport &&
                            matchingSegment.totalsByReport[0].count.value;

                        const bucketSubtype = matchingSegment && matchingSegment.bucketSubtype;

                        if (bucketSubtype === 'percentage') {
                            matchingSegmentCount = `${Number(matchingSegmentCount).toFixed(1)}%`;
                        } else if (matchingSegmentCount != null) {
                            matchingSegmentCount = Number(matchingSegmentCount).toLocaleString();
                        }

                        return (
                            <CellBody rowType={cell.value.rowType} segmentTitle={segmentName}>
                                {matchingSegmentCount}
                            </CellBody>
                        );
                    },
                    // @ts-expect-error
                    Footer: (tableData) => {
                        const segment =
                            totals &&
                            // @ts-expect-error
                            totals.currentPeriod.segments.find((s) => s.title === segmentName);

                        if (!segment) {
                            return '-';
                        }

                        const cell =
                            tableData.rows &&
                            tableData.rows[0].values['stage-task-user'].segments.find(
                                // @ts-expect-error
                                (s) => s.title === segmentName
                            );
                        const subtype = cell && cell.bucketSubtype;
                        const value = segment.totalsByReport[0].count.value;

                        if (subtype === 'percentage') {
                            return (
                                <CellBody rowType='total' segmentTitle={segmentName}>
                                    {`${Number(value).toFixed(1)}%`}
                                </CellBody>
                            );
                        }

                        return (
                            <CellBody rowType='total' segmentTitle={segmentName}>
                                {Number(value).toLocaleString()}
                            </CellBody>
                        );
                    },
                });
            });

        return cols;
    }, [data, totals]);

    return (
        <div className={styles['tasks-table-container']}>
            <ReportSummaryTable
                // @ts-expect-error
                columns={columns}
                data={data}
                emptyStateText={{
                    title: 'No tasks',
                    subtitle: 'Create automated tasks in your pipelines to see them here',
                }}
                hasError={hasError}
                isLoading={isLoading}
                disableSorting={true}
                isResizable={false}
                shouldFooterAlign={true}
                extendToFitWidth={false}
            />
        </div>
    );
}
